import { useRef, useState } from "react";
import { toast } from "sonner";
import { Camera, Loader2, X, Image as ImageIcon } from "lucide-react";
import { api } from "@/lib/api";
import useProofImage from "@/hooks/useProofImage";
import Lightbox from "@/components/Lightbox";

const ACCEPTED_TYPES = ["image/png", "image/jpeg", "image/webp"];
const MAX_BYTES = 5 * 1024 * 1024;

export default function ProofBadge({ planId, actionIndex, proof, onChange }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const hasProof = proof && !proof.is_deleted;
  const preview = useProofImage(planId, actionIndex, hasProof, proof?.uploaded_at);

  const onPick = () => inputRef.current?.click();

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!ACCEPTED_TYPES.includes(f.type)) {
      toast.error("Only PNG, JPEG or WebP");
      return;
    }
    if (f.size > MAX_BYTES) {
      toast.error("Max 5 MB");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post(`/plans/${planId}/actions/${actionIndex}/proof`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange({ uploaded_at: data.uploaded_at, content_type: f.type, is_deleted: false });
      toast.success("Ship-proof saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const remove = async () => {
    try {
      await api.delete(`/plans/${planId}/actions/${actionIndex}/proof`);
      onChange(null);
      toast.success("Proof removed");
    } catch {
      toast.error("Could not remove");
    }
  };

  return (
    <div className="shrink-0 flex items-center gap-2" data-testid={`proof-widget-${actionIndex}`}>
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        className="hidden"
        onChange={onFile}
        data-testid={`proof-input-${actionIndex}`}
      />
      {hasProof ? (
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setLightboxOpen(true)}
            data-testid={`proof-thumb-${actionIndex}`}
            className="relative h-12 w-12 rounded-lg overflow-hidden border-2 border-emerald-400 bg-zinc-100 flex items-center justify-center"
            title="View ship-proof"
          >
            {preview ? (
              <img src={preview} alt="ship proof" className="h-full w-full object-cover" />
            ) : (
              <ImageIcon className="h-4 w-4 text-zinc-400" />
            )}
          </button>
          <button
            type="button"
            onClick={remove}
            data-testid={`proof-remove-${actionIndex}`}
            className="text-xs text-zinc-500 hover:text-rose-600"
            title="Remove proof"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={onPick}
          disabled={busy}
          data-testid={`proof-upload-${actionIndex}`}
          className="inline-flex items-center gap-1.5 text-xs font-medium px-3 h-9 rounded-full border border-dashed border-zinc-300 hover:border-indigo-500 hover:text-indigo-600 text-zinc-600 bg-white"
        >
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Camera className="h-3.5 w-3.5" />}
          {busy ? "Uploading" : "Ship-proof"}
        </button>
      )}
      <Lightbox
        src={lightboxOpen ? preview : null}
        onClose={() => setLightboxOpen(false)}
        testId={`proof-lightbox-${actionIndex}`}
      />
    </div>
  );
}
